import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { UserContext } from '../../context/UserContext';
import { getActiveUsersDetails } from '../../services/sessionService';
import { formatDistanceToNow } from 'date-fns';
import { Role } from '../../utils/enums';
import './ActiveUsersDetails.css';

// Helper to get role name from role ID
const getRoleName = (roleId) => {
  const roleName = Object.keys(Role).find((key) => Role[key] === roleId);
  switch (roleName) {
    case 'Admin':
      return 'Admin';
    case 'Seller':
      return 'Seller';
    case 'User':
      return 'User';
    case 'Guest':
      return 'Guest';
    default:
      return 'Unknown';
  }
};

const ActiveUsersDetails = ({ isOpen, onClose }) => {
  const { user } = useContext(UserContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        // 1. Fetch the raw user details from the backend
        const rawUsers = await getActiveUsersDetails(user.token);

        // 2. Enhance the data with country lookups
        const enhancedUsers = await Promise.all(
          rawUsers.map(async (u) => {
            const fullName = u.firstName || u.lastName ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : 'Guest';
            return {
              ...u,
              fullName,
            };
          })
        );

        // 3. Sort the final list by full name
        enhancedUsers.sort((a, b) => a.fullName.localeCompare(b.fullName));

        setUsers(enhancedUsers);
      } catch (err) {
        console.error('Failed to fetch active user details:', err); // Log the actual error
        setError('Failed to load user details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails(); // Call fetchDetails inside useEffect
  }, [isOpen, user.token]); // Removed 't' from dependency array

  if (!isOpen) {
    return null;
  }

  return (
    <div className="active-users-modal-overlay" onClick={onClose}>
      <div className="active-users-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="active-users-modal-header">
          <h2>Active Users</h2>
          <button type="button" onClick={onClose} className="active-users-modal-close-btn">
            &times;
          </button>
        </div>
        <div className="active-users-modal-body">
          {loading && <p>Loading user details...</p>}
          {error && <p className="error-message">{error}</p>}
          {!loading && !error && (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Username</th>
                    <th>IP Address</th>
                    <th>Country</th>
                    <th>Sessions (from IP)</th>
                    <th>Role</th>
                    <th>Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.sessionId}>
                      <td>{u.fullName}</td>
                      <td>{u.username}</td>
                      <td>{u.ip}</td>
                      <td>{u.country}</td>
                      <td className="sessions-count">{u.sessionCountForIp}</td>
                      <td>{getRoleName(u.role)}</td>
                      <td>{formatDistanceToNow(new Date(u.lastSeen), { addSuffix: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

ActiveUsersDetails.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default ActiveUsersDetails;
